import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronLeft, Check, Car, Truck, Caravan, MapPin, Home, Phone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "../ui/calendar";
import { PACKAGES, ADDONS, VEHICLE_TYPES, BUSINESS } from "../../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIME_SLOTS = ["8:00 AM", "10:00 AM", "12:30 PM", "3:00 PM"];
const TYPE_ICONS = { Sedan: Car, SUV: Caravan, Truck: Truck };
const STEPS = ["Vehicle", "Package", "Add-ons", "Location", "Schedule", "Details", "Summary"];

const emptyForm = {
  vehicleType: "Sedan", vehicle_make: "", vehicle_model: "", vehicle_year: "",
  packageId: "full", addOns: [], serviceMode: "mobile", location: "",
  date: null, time: "", full_name: "", phone: "", email: "", notes: "", referral: "",
};

export default function Booking({ open, onClose, prefill }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // apply prefill when opened
  const [lastPrefill, setLastPrefill] = useState(null);
  if (open && prefill && prefill !== lastPrefill) {
    setLastPrefill(prefill);
    setForm((f) => ({ ...f, ...(prefill.vehicleType && { vehicleType: prefill.vehicleType }), ...(prefill.packageId && { packageId: prefill.packageId }) }));
    setStep(0);
    setDone(false);
  }

  const pkg = PACKAGES.find((p) => p.id === form.packageId);
  const estimate = useMemo(() => {
    const base = pkg ? pkg.prices[form.vehicleType] : 0;
    const addTotal = form.addOns.reduce((s, id) => s + (ADDONS.find((a) => a.id === id)?.from || 0), 0);
    return { base, addTotal, total: base + addTotal };
  }, [pkg, form.vehicleType, form.addOns]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAddon = (id) =>
    setForm((f) => ({ ...f, addOns: f.addOns.includes(id) ? f.addOns.filter((x) => x !== id) : [...f.addOns, id] }));

  const canNext = () => {
    switch (step) {
      case 0: return form.vehicle_make && form.vehicle_model && form.vehicle_year;
      case 3: return form.location.trim().length > 2;
      case 4: return form.date && form.time;
      case 5: return form.full_name.trim() && form.phone.trim().length >= 7;
      default: return true;
    }
  };

  const reset = () => { setForm(emptyForm); setStep(0); setDone(false); setLastPrefill(null); };
  const close = () => { onClose(); setTimeout(reset, 300); };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        vehicle_make: form.vehicle_make, vehicle_model: form.vehicle_model,
        vehicle_year: form.vehicle_year, vehicle_type: form.vehicleType,
        package_name: pkg.name, package_price: estimate.base,
        add_ons: form.addOns.map((id) => { const a = ADDONS.find((x) => x.id === id); return { name: a.name, price: a.range }; }),
        service_mode: form.serviceMode,
        appointment_date: form.date ? new Date(form.date).toISOString().slice(0, 10) : "",
        appointment_time: form.time, location: form.location,
        full_name: form.full_name, phone: form.phone, email: form.email,
        notes: form.notes, referral: form.referral,
        estimated_total: `From $${estimate.total}`,
      };
      await axios.post(`${API}/bookings`, payload);
      setDone(true);
    } catch (e) {
      toast.error("Couldn't submit booking. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full border-b-2 border-ink/20 bg-transparent py-2.5 text-ink placeholder:text-ink/40 focus:border-ink focus:outline-none";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-ink/50 backdrop-blur-sm sm:items-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={close}
          data-testid="booking-overlay"
        >
          <motion.div
            className="flex h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-cream sm:h-[88vh] sm:rounded-3xl"
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="booking-modal"
          >
            {/* header */}
            <div className="flex items-center justify-between border-b border-ink/10 px-5 py-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-ink/50">
                  {done ? "Confirmed" : `Step ${step + 1} / ${STEPS.length}`}
                </p>
                <p className="font-display text-lg leading-none">{done ? "You're booked" : STEPS[step]}</p>
              </div>
              <button data-testid="booking-close" onClick={close} className="rounded-full border border-ink/20 p-2">
                <X size={18} />
              </button>
            </div>

            {!done && (
              <div className="h-1 w-full bg-ink/10">
                <div className="h-full bg-ink transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
              </div>
            )}

            {/* body */}
            <div className="flex-1 overflow-y-auto px-5 py-6">
              {done ? (
                <div className="flex h-full flex-col items-center justify-center text-center" data-testid="booking-success">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-ink text-cream">
                    <Check size={30} />
                  </div>
                  <h3 className="mt-6 font-display text-3xl leading-tight">Request sent</h3>
                  <p className="mt-3 max-w-xs text-ink/70">
                    I&apos;ll confirm your {pkg.name} appointment by phone. Estimated total{" "}
                    <span className="font-bold">from ${estimate.total}</span>, paid in person.
                  </p>
                  <a href={BUSINESS.phoneHref} className="mt-6 inline-flex items-center gap-2 rounded-full border border-ink/25 px-6 py-3 text-sm font-bold uppercase tracking-widest">
                    <Phone size={15} /> {BUSINESS.phone}
                  </a>
                  <button onClick={close} data-testid="booking-done" className="mt-3 rounded-full bg-ink px-8 py-3 text-sm font-bold uppercase tracking-widest text-cream">Done</button>
                </div>
              ) : (
                <>
                  {/* STEP 0 vehicle */}
                  {step === 0 && (
                    <div className="space-y-6">
                      <div>
                        <p className="mb-3 text-xs font-bold uppercase tracking-widest text-ink/50">Vehicle type</p>
                        <div className="grid grid-cols-3 gap-3">
                          {VEHICLE_TYPES.map((t) => {
                            const Icon = TYPE_ICONS[t];
                            const active = form.vehicleType === t;
                            return (
                              <button key={t} data-testid={`vehicle-type-${t.toLowerCase()}`} onClick={() => set("vehicleType", t)}
                                className={`flex flex-col items-center gap-2 rounded-2xl border p-4 transition-colors ${active ? "border-ink bg-ink text-cream" : "border-ink/20"}`}>
                                <Icon size={22} />
                                <span className="text-xs font-bold uppercase tracking-widest">{t}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <input data-testid="input-make" className={inputCls} placeholder="Make (e.g. Toyota)" value={form.vehicle_make} onChange={(e) => set("vehicle_make", e.target.value)} />
                      <input data-testid="input-model" className={inputCls} placeholder="Model (e.g. Camry)" value={form.vehicle_model} onChange={(e) => set("vehicle_model", e.target.value)} />
                      <input data-testid="input-year" className={inputCls} placeholder="Year (e.g. 2019)" inputMode="numeric" value={form.vehicle_year} onChange={(e) => set("vehicle_year", e.target.value)} />
                    </div>
                  )}

                  {/* STEP 1 package */}
                  {step === 1 && (
                    <div className="space-y-3">
                      {PACKAGES.map((p) => {
                        const active = form.packageId === p.id;
                        return (
                          <button key={p.id} data-testid={`select-package-${p.id}`} onClick={() => set("packageId", p.id)}
                            className={`flex w-full items-center justify-between rounded-2xl border p-4 text-left transition-colors ${active ? "border-ink bg-ink text-cream" : "border-ink/20"}`}>
                            <div>
                              <p className="font-display text-base leading-tight">{p.name}</p>
                              <p className={`mt-1 text-xs ${active ? "text-cream/60" : "text-ink/50"}`}>{p.desc}</p>
                            </div>
                            <span className="font-display text-2xl">${p.prices[form.vehicleType]}{p.truckPlus && form.vehicleType === "Truck" ? "+" : ""}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* STEP 2 addons */}
                  {step === 2 && (
                    <div className="space-y-3">
                      <p className="text-sm text-ink/60">Optional extras. Prices vary — final quote confirmed by phone.</p>
                      {ADDONS.map((a) => {
                        const active = form.addOns.includes(a.id);
                        return (
                          <button key={a.id} data-testid={`addon-${a.id}`} onClick={() => toggleAddon(a.id)}
                            className={`flex w-full items-center justify-between rounded-2xl border p-4 transition-colors ${active ? "border-ink bg-ink text-cream" : "border-ink/20"}`}>
                            <span className="font-semibold">{a.name}</span>
                            <span className="flex items-center gap-3 text-sm font-bold">{a.range}
                              <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${active ? "border-cream bg-cream text-ink" : "border-ink/30"}`}>{active && <Check size={13} />}</span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* STEP 3 location */}
                  {step === 3 && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 gap-3">
                        <button data-testid="mode-mobile" onClick={() => set("serviceMode", "mobile")}
                          className={`flex flex-col items-center gap-2 rounded-2xl border p-5 transition-colors ${form.serviceMode === "mobile" ? "border-ink bg-ink text-cream" : "border-ink/20"}`}>
                          <MapPin size={22} /><span className="text-xs font-bold uppercase tracking-widest">We come to you</span>
                        </button>
                        <button data-testid="mode-dropoff" onClick={() => set("serviceMode", "dropoff")}
                          className={`flex flex-col items-center gap-2 rounded-2xl border p-5 transition-colors ${form.serviceMode === "dropoff" ? "border-ink bg-ink text-cream" : "border-ink/20"}`}>
                          <Home size={22} /><span className="text-xs font-bold uppercase tracking-widest">Drop-off</span>
                        </button>
                      </div>
                      <input data-testid="input-location" className={inputCls}
                        placeholder={form.serviceMode === "mobile" ? "Your address" : "Drop-off area / notes"}
                        value={form.location} onChange={(e) => set("location", e.target.value)} />
                      {form.serviceMode === "dropoff" && (
                        <p data-testid="dropoff-note" className="rounded-2xl bg-ink/5 p-4 text-sm text-ink/70">
                          For drop-off, <a href={BUSINESS.phoneHref} className="font-bold underline">call</a> or{" "}
                          <a href={BUSINESS.smsHref} className="font-bold underline">text {BUSINESS.phone}</a> for the address.
                        </p>
                      )}
                    </div>
                  )}

                  {/* STEP 4 schedule */}
                  {step === 4 && (
                    <div className="space-y-5">
                      <div className="flex justify-center rounded-2xl border border-ink/15 p-2" data-testid="booking-calendar">
                        <Calendar mode="single" selected={form.date} onSelect={(d) => set("date", d)}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 6} />
                      </div>
                      <p className="text-center text-xs font-semibold uppercase tracking-widest text-ink/50">Closed Saturdays</p>
                      <div>
                        <p className="mb-2 text-xs font-bold uppercase tracking-widest text-ink/50">Time (detail takes {BUSINESS.detailHours})</p>
                        <div className="grid grid-cols-2 gap-3">
                          {TIME_SLOTS.map((t) => (
                            <button key={t} data-testid={`time-${t}`} onClick={() => set("time", t)}
                              className={`rounded-full border py-3 text-sm font-bold uppercase tracking-widest transition-colors ${form.time === t ? "border-ink bg-ink text-cream" : "border-ink/20"}`}>{t}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 5 details */}
                  {step === 5 && (
                    <div className="space-y-5">
                      <input data-testid="input-name" className={inputCls} placeholder="Full name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
                      <input data-testid="input-phone" className={inputCls} placeholder="Phone number" inputMode="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                      <input data-testid="input-email" className={inputCls} placeholder="Email (optional)" value={form.email} onChange={(e) => set("email", e.target.value)} />
                      <input data-testid="input-referral" className={inputCls} placeholder="Referred by? (optional)" value={form.referral} onChange={(e) => set("referral", e.target.value)} />
                      <textarea data-testid="input-notes" rows={2} className={inputCls + " resize-none"} placeholder="Anything I should know? (optional)" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
                    </div>
                  )}

                  {/* STEP 6 summary */}
                  {step === 6 && (
                    <div className="space-y-4" data-testid="booking-summary">
                      <div className="rounded-2xl bg-ink p-6 text-cream">
                        <Row k="Vehicle" v={`${form.vehicle_year} ${form.vehicle_make} ${form.vehicle_model} · ${form.vehicleType}`} />
                        <Row k="Package" v={`${pkg.name}`} />
                        <Row k="Base price" v={`$${estimate.base}`} />
                        {form.addOns.length > 0 && <Row k="Add-ons" v={form.addOns.map((id) => ADDONS.find((a) => a.id === id)?.name).join(", ")} />}
                        <Row k="Service" v={form.serviceMode === "mobile" ? "We come to you" : "Drop-off"} />
                        <Row k="Location" v={form.location} />
                        <Row k="When" v={`${form.date ? new Date(form.date).toDateString() : ""} · ${form.time}`} />
                        <Row k="Name" v={form.full_name} />
                        <Row k="Phone" v={form.phone} />
                        <div className="mt-4 flex items-end justify-between border-t border-cream/20 pt-4">
                          <span className="text-xs font-bold uppercase tracking-widest text-cream/60">Estimated total</span>
                          <span className="font-display text-4xl">from ${estimate.total}</span>
                        </div>
                      </div>
                      <p className="text-center text-xs uppercase tracking-widest text-ink/50">Pay in person · I&apos;ll confirm by phone</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* footer nav */}
            {!done && (
              <div className="flex items-center gap-3 border-t border-ink/10 px-5 py-4">
                {step > 0 && (
                  <button data-testid="booking-back" onClick={() => setStep((s) => s - 1)}
                    className="flex items-center gap-1 rounded-full border border-ink/25 px-5 py-3 text-sm font-bold uppercase tracking-widest">
                    <ChevronLeft size={16} /> Back
                  </button>
                )}
                <div className="flex-1 text-right text-sm">
                  {step >= 1 && <span className="font-display text-2xl">from ${estimate.total}</span>}
                </div>
                {step < STEPS.length - 1 ? (
                  <button data-testid="booking-next" disabled={!canNext()} onClick={() => canNext() && setStep((s) => s + 1)}
                    className="flex items-center gap-1 rounded-full bg-ink px-7 py-3 text-sm font-bold uppercase tracking-widest text-cream transition-transform active:scale-95 disabled:opacity-40">
                    Next <ChevronRight size={16} />
                  </button>
                ) : (
                  <button data-testid="booking-confirm" disabled={submitting} onClick={submit}
                    className="rounded-full bg-ink px-7 py-3 text-sm font-bold uppercase tracking-widest text-cream transition-transform active:scale-95 disabled:opacity-50">
                    {submitting ? "Sending…" : "Confirm Booking"}
                  </button>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm">
    <span className="shrink-0 text-cream/50">{k}</span>
    <span className="text-right font-medium">{v}</span>
  </div>
);
